import { IconTypes } from '@stockmarkteam/donguri-ui';
import { BlobReader, BlobWriter, ZipWriter } from '@zip.js/zip.js';
import { filesize } from 'filesize';
import { FileType } from '@/types';

export const FILE_PART_SIZE = 50 * 1024 * 1024;

// -1:自動, 0-8: B(byte), KB, MB, GB, TB, PB, EB, ZB, YB で固定表記される.
type FixExponent = 0 | 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8;
type AutoExponent = -1;
export const formatUsageFileSize = (
  bytes: number,
  exponents: FixExponent | FixExponent[] | AutoExponent = [4, 3, 2],
) => {
  const exponent = Array.isArray(exponents)
    ? exponents.toSorted((a, b) => b - a) // 降順ソートして最も指定された単位が最も上のものから表示する
    : exponents;

  return filesize(bytes, {
    base: 2,
    standard: 'jedec',
    round: 1,
    output: 'string',
    exponent: Array.isArray(exponent)
      ? exponent.find(e => bytes >= 1024 ** e) ?? 2
      : exponent,
  });
};

// zipファイルを作る
export const createZipFile = async (
  files: File[],
  abortSignal: AbortSignal,
): Promise<File> => {
  const zipWriter = new ZipWriter(new BlobWriter('application/zip'), {
    bufferedWrite: true,
    level: 6,
    signal: abortSignal,
  });

  for (const item of files) {
    await zipWriter.add(item.name, new BlobReader(item));
  }

  const zipBlob = await zipWriter.close();
  return new File([zipBlob], 'data.zip', { type: 'application/zip' });
};

// ファイル分割。参考: https://github.com/t-kigi/s3-multipart-uploads-with-chalice/blob/main/js/upload.js
export const readPartData = async (file: File, index: number) => {
  return new Promise<Uint8Array>((resolve, _reject) => {
    const reader = new FileReader();
    const offset = index * FILE_PART_SIZE;
    const slice = file.slice(offset, offset + FILE_PART_SIZE, file.type);
    reader.readAsArrayBuffer(slice);

    reader.onload = e => {
      const data = new Uint8Array(e.target?.result as ArrayBuffer);
      resolve(data);
      reader.abort();
    };
  });
};

// ディレクトリの中身のファイルを読み込む
export const processFileEntry = async (
  entry: FileSystemEntry,
  shouldReadDirectory?: (name: string) => boolean,
): Promise<File[]> => {
  const results: File[] = [];

  if (entry.isFile) {
    const fileEntry = entry as FileSystemFileEntry;
    const file = await new Promise<File>((resolve, reject) => {
      fileEntry.file(resolve, reject);
    });
    results.push(file);
  } else if (entry.isDirectory) {
    if (shouldReadDirectory && !shouldReadDirectory(entry.name)) return [];
    const dirEntry = entry as FileSystemDirectoryEntry;
    const reader = dirEntry.createReader();

    // ディレクトリの全エントリを読み込むまで繰り返す
    let entries: FileSystemEntry[] = [];
    let readBatch: FileSystemEntry[];

    do {
      readBatch = await new Promise<FileSystemEntry[]>((resolve, reject) => {
        reader.readEntries(resolve, reject);
      });
      entries.push(...readBatch);
    } while (readBatch.length > 0);

    for (const childEntry of entries) {
      const childResults = await processFileEntry(
        childEntry,
        shouldReadDirectory,
      );
      results.push(...childResults);
    }
  }
  return results;
};

export const readDroppedFiles = (
  transfer: DataTransfer,
  shouldReadDirectory?: (name: string) => boolean,
): Promise<File[]> => {
  // DataTransferはイベント終了後に読めないため、非同期処理より先に退避する。
  const items = Array.from(transfer.items ?? []).filter(
    item => item.kind === 'file',
  );
  const captured = items.map(item => ({
    entry: item.webkitGetAsEntry?.(),
    file: item.getAsFile(),
  }));
  const fallback = Array.from(transfer.files);
  if (!captured.length) return Promise.resolve(fallback);
  return Promise.all(
    captured.map(item =>
      item.entry
        ? processFileEntry(item.entry, shouldReadDirectory)
        : Promise.resolve(item.file ? [item.file] : []),
    ),
  ).then(groups => groups.flat());
};

export const fileIconName = (fileType: FileType): IconTypes => {
  switch (fileType) {
    case 'PDF':
      return 'file-pdf-fill-color';
    case 'Word':
      return 'file-word-fill-color';
    case 'PowerPoint':
      return 'file-powerpoint-fill-color';
    case 'Excel':
      return 'file-excel-fill-color';
    case 'unknown':
      return 'file-v';
  }
};

export const range = (start: number, end: number) =>
  Array.from({ length: end - start + 1 }, (_v, k) => k + start);
